import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Printer, Store, Crown, LogOut, Zap, History, Moon, Sun, Shield, Menu, LogIn } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export function Navbar({ onOpenStore, onOpenPricing, onOpenHistory, onOpenAdmin, usage, isDark, onToggleDark, isAdmin }) {
  const { user, login, logout, isPro } = useAuth();

  return (
    <header
      data-testid="top-navbar"
      className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-md"
    >
      <div className="mx-auto flex min-h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:px-6 lg:px-8">
        <a href="/" data-testid="brand-logo-link" className="flex min-w-0 shrink-0 items-center gap-2 group">
          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-sky-600 text-white shadow-sm transition-colors group-hover:bg-sky-700 sm:h-9 sm:w-9">
            <Printer size={20} strokeWidth={2.5} />
          </div>
          <div className="min-w-0 leading-none">
            <div className="truncate font-extrabold text-base tracking-tight text-slate-900 sm:text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
              ResiCetak<span className="text-sky-600">.work</span>
            </div>
            <div className="mt-1 hidden text-[10px] font-mono uppercase tracking-widest text-slate-400 sm:block">Thermal Label Generator</div>
          </div>
        </a>

        <nav aria-label="Navigasi utama" className="hidden shrink-0 items-center gap-2 lg:flex xl:gap-3">
          {usage && usage.limit !== null && (
            <Badge variant="outline" data-testid="usage-badge" className="border-slate-300 font-mono text-slate-600">
              {usage.remaining}/{usage.limit} resi hari ini
            </Badge>
          )}
          {isAdmin && (
            <Button data-testid="nav-admin-btn" variant="outline" size="sm" onClick={onOpenAdmin}
              className="gap-1.5 border-sky-300 text-sky-700">
              <Shield size={16} /> <span className="hidden sm:inline">Admin</span>
            </Button>
          )}
          <Button data-testid="nav-dark-toggle" variant="outline" size="icon" onClick={onToggleDark} title="Mode gelap">
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </Button>
          <Button data-testid="nav-history-btn" variant="outline" size="sm" onClick={onOpenHistory} className="gap-1.5">
            <History size={16} /> <span className="hidden sm:inline">Riwayat</span>
          </Button>
          <Button data-testid="nav-store-btn" variant="outline" size="sm" onClick={onOpenStore} className="gap-1.5">
            <Store size={16} /> <span className="hidden sm:inline">Toko Saya</span>
          </Button>

          {!isPro && (
            <Button data-testid="pro-upgrade-cta-header" size="sm" onClick={onOpenPricing}
              className="gap-1.5 bg-orange-600 hover:bg-orange-700 text-white">
              <Zap size={16} /> Upgrade Pro
            </Button>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border border-slate-200 pl-1 pr-2 py-1 hover:bg-slate-50 transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="h-7 w-7 rounded-full" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-sky-600 text-white grid place-items-center text-xs font-bold">
                      {(user.name || "U")[0]}
                    </div>
                  )}
                  {isPro && <Crown size={14} className="text-orange-500" />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <div className="font-semibold text-sm truncate">{user.name}</div>
                  <div className="text-xs text-slate-500 truncate">{user.email}</div>
                  {isPro && <Badge className="mt-1 bg-orange-100 text-orange-700 hover:bg-orange-100">PRO</Badge>}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="gap-2 text-red-600">
                  <LogOut size={16} /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="google-login-button" size="sm" variant="outline" onClick={login} className="gap-2 font-semibold">
              <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
              <span className="hidden sm:inline">Masuk</span>
            </Button>
          )}
        </nav>

        <div className="lg:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" aria-label="Buka menu" title="Buka menu">
                <Menu size={18} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className="w-60">
              {usage && usage.limit !== null && (
                <>
                  <div className="px-2 py-1.5 text-xs font-mono text-slate-500">
                    {usage.remaining}/{usage.limit} resi hari ini
                  </div>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onSelect={onOpenHistory} className="gap-2">
                <History size={16} /> Riwayat
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onOpenStore} className="gap-2">
                <Store size={16} /> Toko Saya
              </DropdownMenuItem>
              {!isPro && (
                <DropdownMenuItem onSelect={onOpenPricing} className="gap-2 text-orange-700">
                  <Zap size={16} /> Upgrade Pro
                </DropdownMenuItem>
              )}
              {isAdmin && (
                <DropdownMenuItem onSelect={onOpenAdmin} className="gap-2">
                  <Shield size={16} /> Admin
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={onToggleDark} className="gap-2">
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
                {isDark ? "Mode terang" : "Mode gelap"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {user ? (
                <>
                  <div className="px-2 py-1.5">
                    <div className="truncate text-sm font-semibold">{user.name}</div>
                    <div className="truncate text-xs text-slate-500">{user.email}</div>
                    {isPro && <Badge className="mt-1 bg-orange-100 text-orange-700 hover:bg-orange-100">PRO</Badge>}
                  </div>
                  <DropdownMenuItem data-testid="logout-btn-mobile" onSelect={logout} className="gap-2 text-red-600">
                    <LogOut size={16} /> Keluar
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem data-testid="google-login-button-mobile" onSelect={login} className="gap-2">
                  <LogIn size={16} /> Masuk dengan Google
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
